import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, LinearProgress, IconButton,
  Tooltip, Collapse, Badge, Button, useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DuplicateDetectionDialog from './DuplicateDetectionDialog';
import { useUpload } from '../contexts/UploadContext';

const VENDOR_LABELS = {
  american_eagle: 'American Eagle',
  chico:          "Chico's",
  freepeople:     'Free People',
  lane_bryant:    'Lane Bryant',
  mast:           'MAST',
  loft:           'LOFT',
  talbots:        'Talbots',
};

const VENDOR_COLORS = {
  american_eagle: '#c41230',
  chico:          '#7c3aed',
  freepeople:     '#d97706',
  lane_bryant:    '#1d4ed8',
  mast:           '#059669',
  loft:           '#b45309',
  talbots:        '#be123c',
};

function StatusIcon({ status }) {
  const sz = { fontSize: 16 };
  if (status === 'done')       return <CheckCircleIcon  sx={{ ...sz, color: 'success.main' }} />;
  if (status === 'error')      return <ErrorIcon        sx={{ ...sz, color: 'error.main'   }} />;
  if (status === 'duplicate')  return <WarningAmberIcon sx={{ ...sz, color: 'warning.main' }} />;
  if (status === 'processing') return (
    <HourglassEmptyIcon sx={{
      ...sz, color: 'primary.main',
      animation: 'widgetSpin 1.5s linear infinite',
    }} />
  );
  return <HourglassEmptyIcon sx={{ ...sz, color: 'text.disabled' }} />;
}

function JobRow({ job, onDismiss, onDownload, onOpenDuplicate, navigate }) {
  const color = VENDOR_COLORS[job.vendor] || '#6366f1';
  const label = VENDOR_LABELS[job.vendor] || job.vendor;
  const pct   = job.total > 0 ? Math.round((job.progress / job.total) * 100) : 0;
  const revs  = job.revision_updates || [];

  return (
    <Box sx={{
      p: 1.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-child': { borderBottom: 0 },
    }}>
      {/* Row header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        <StatusIcon status={job.status} />

        <Box sx={{
          px: 0.75, py: 0.15, borderRadius: '5px',
          fontSize: '0.68rem', fontWeight: 700,
          bgcolor: `${color}22`, color, flexShrink: 0,
        }}>
          {label}
        </Box>

        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, fontSize: '0.72rem' }}>
          {job.filenames.length === 1 ? job.filenames[0] : `${job.filenames.length} files`}
        </Typography>

        {job.status === 'done' && (
          <Tooltip title="Download Excel">
            <IconButton size="small" onClick={() => onDownload(job.id)} sx={{ p: 0.25 }}>
              <DownloadIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Dismiss">
          <IconButton size="small" onClick={() => onDismiss(job.id)} sx={{ p: 0.25 }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Progress bar */}
      {['queued', 'processing'].includes(job.status) && (
        <LinearProgress
          variant={job.status === 'queued' ? 'indeterminate' : 'determinate'}
          value={pct}
          sx={{ borderRadius: 2, height: 3, mb: 0.4 }}
        />
      )}

      {/* Status text */}
      <Typography
        variant="caption"
        color={
          job.status === 'error'     ? 'error.main'   :
          job.status === 'duplicate' ? 'warning.main' :
          job.status === 'done'      ? 'success.main' :
          'text.secondary'
        }
        sx={{ fontSize: '0.7rem', display: 'block' }}
      >
        {job.status === 'processing' && `${pct}% — `}
        {job.message || job.status}
      </Typography>

      {/* Duplicate action row */}
      {job.status === 'duplicate' && (
        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
            onClick={() => onOpenDuplicate(job)}
          >
            View Diff & Override
          </Button>
          {job.duplicate_info?.duplicates?.[0]?.existing_po_id && (
            <Button
              size="small"
              variant="text"
              color="primary"
              sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75 }}
              onClick={() => {
                const dups = job.duplicate_info.duplicates;
                const batch = dups.map(d => d.existing_po_id);
                const poNumberMap = Object.fromEntries(
                  dups.map(d => [String(d.existing_po_id), d.po_number || String(d.existing_po_id)])
                );
                navigate(`/audit/${batch[0]}`, { state: { processedPoIds: batch, poNumberMap } });
              }}
            >
              View in Audit
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            color="inherit"
            sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, color: 'text.secondary' }}
            onClick={() => onDismiss(job.id)}
          >
            Dismiss
          </Button>
        </Box>
      )}

      {/* Audit redirect when done OR mixed duplicate+new */}
      {(job.status === 'done' || (job.status === 'duplicate' && Array.isArray(job.result_po_ids) && job.result_po_ids.length > 0)) && job.processed_files > 0 && (
        <Box sx={{ mt: 0.75 }}>
          {job.status === 'duplicate' && job.result_po_ids?.length > 0 && (
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', display: 'block', mb: 0.4 }}>
              New POs saved:
            </Typography>
          )}
          {/* Individual PO links — one chip per processed PO */}
          {Array.isArray(job.result_po_ids) && job.result_po_ids.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: revs.length > 0 ? 0.5 : 0 }}>
              {job.result_po_ids.map((po) => (
                <Tooltip key={po.id} title={`Open audit for PO ${po.customer_po_number || po.id}`}>
                  <Box
                    onClick={() => navigate(`/audit/${po.id}`)}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.4,
                      px: 0.9, py: 0.3, borderRadius: '6px', cursor: 'pointer',
                      fontSize: '0.68rem', fontWeight: 700,
                      bgcolor: 'rgba(99,102,241,0.1)', color: 'primary.main',
                      border: '1px solid rgba(99,102,241,0.25)',
                      transition: 'background 0.15s, border-color 0.15s',
                      '&:hover': {
                        bgcolor: 'rgba(99,102,241,0.2)',
                        borderColor: 'rgba(99,102,241,0.5)',
                      },
                    }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 10 }} />
                    PO {po.customer_po_number || po.id}
                  </Box>
                </Tooltip>
              ))}
            </Box>
          ) : (
            /* Fallback: backend didn't return IDs — go to review queue */
            <Button
              size="small"
              variant="text"
              sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, color: 'primary.main' }}
              onClick={() => navigate('/navigation')}
            >
              View Audit Queue
            </Button>
          )}
          {revs.length > 0 && (
            <Button
              size="small"
              variant="text"
              startIcon={<HistoryIcon sx={{ fontSize: 12 }} />}
              sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, color: 'text.secondary' }}
              onClick={() => navigate('/revisions')}
            >
              {revs.length} revision{revs.length !== 1 ? 's' : ''} updated
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

export default function UploadProgressWidget() {
  const theme   = useTheme();
  const isDark  = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { jobs, dismissJob, dismissAllSettled, downloadJob, overrideJob, fieldOverrideJob } = useUpload();
  const [expanded, setExpanded] = useState(true);
  const [dupDialog, setDupDialog] = useState({ open: false, job: null });
  const seenDupIds = useRef(new Set());

  // Auto-expand widget when a job hits duplicate or error
  useEffect(() => {
    jobs.forEach(j => {
      if (!j.dismissed && !seenDupIds.current.has(j.id) &&
          (j.status === 'duplicate' || j.status === 'error')) {
        seenDupIds.current.add(j.id);
        setExpanded(true);
      }
    });
  }, [jobs]);

  const visible = jobs.filter(j => !j.dismissed);
  if (visible.length === 0) return null;

  const active = visible.filter(j => ['queued', 'processing'].includes(j.status));
  const done   = visible.filter(j => j.status === 'done').length;
  const issues = visible.filter(j => ['error', 'duplicate'].includes(j.status)).length;

  const handleOpenDuplicate = (job) => setDupDialog({ open: true, job });
  const handleCloseDuplicate = () => setDupDialog({ open: false, job: null });

  // User chose "Keep All Existing" — dismiss the widget entry, nothing to reprocess
  const handleKeepAll = () => {
    if (dupDialog.job) dismissJob(dupDialog.job.id);
    handleCloseDuplicate();
  };

  // filenamesToOverride: string[] from per-PO decisions in the dialog (legacy full override)
  const handleOverride = async (filenamesToOverride) => {
    if (!dupDialog.job) return;
    const jobId = dupDialog.job.id;
    handleCloseDuplicate();
    try {
      await overrideJob(jobId, filenamesToOverride && filenamesToOverride.length > 0 ? filenamesToOverride : null);
    } catch (e) {
      console.error('Override failed', e);
    }
  };

  // { filenames, field_decisions } — new per-field path from DuplicateDetectionDialog
  const handleFieldOverride = async ({ filenames, field_decisions }) => {
    if (!dupDialog.job) return;
    const jobId = dupDialog.job.id;
    handleCloseDuplicate();
    try {
      await fieldOverrideJob(jobId, { filenames, field_decisions });
    } catch (e) {
      console.error('Field override failed', e);
    }
  };

  return (
    <>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 330,
          zIndex: 1400,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: isDark ? 'rgba(148,163,184,0.15)' : 'rgba(15,23,42,0.1)',
          bgcolor: isDark ? '#0d1526' : '#fff',
          boxShadow: isDark
            ? '0 8px 32px rgba(0,0,0,0.6)'
            : '0 8px 32px rgba(0,0,0,0.15)',
          '@keyframes widgetSpin': {
            '0%':   { transform: 'rotate(0deg)'   },
            '100%': { transform: 'rotate(360deg)' },
          },
        }}
      >
        {/* Widget header */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 1,
            bgcolor: isDark ? 'rgba(99,102,241,0.12)' : 'rgba(79,70,229,0.06)',
            borderBottom: '1px solid', borderColor: 'divider',
            cursor: 'pointer', userSelect: 'none',
          }}
          onClick={() => setExpanded(v => !v)}
        >
          <Badge
            badgeContent={active.length || null}
            color="primary"
            sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
          >
            <CloudUploadIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Badge>

          <Typography variant="body2" fontWeight={700} sx={{ flex: 1, fontSize: '0.8rem' }}>
            {active.length > 0
              ? `Processing ${active.length} job${active.length !== 1 ? 's' : ''}…`
              : `Uploads (${done} done${issues ? `, ${issues} issue${issues !== 1 ? 's' : ''}` : ''})`}
          </Typography>

          {/* Clear all settled (done/error/duplicate) jobs in one click */}
          {visible.some(j => !['queued', 'processing'].includes(j.status)) && (
            <Tooltip title="Clear finished jobs">
              <Button
                size="small"
                onClick={(e) => { e.stopPropagation(); dismissAllSettled(); }}
                sx={{
                  fontSize: '0.65rem',
                  py: 0.15,
                  px: 0.75,
                  minWidth: 0,
                  color: isDark ? '#94a3b8' : '#64748b',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                Clear done
              </Button>
            </Tooltip>
          )}

          <IconButton size="small" sx={{ p: 0.25 }}>
            {expanded
              ? <ExpandMoreIcon sx={{ fontSize: 16 }} />
              : <ExpandLessIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>

        {/* Job list */}
        <Collapse in={expanded}>
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {visible.map(job => (
              <JobRow
                key={job.id}
                job={job}
                navigate={navigate}
                onDismiss={dismissJob}
                onDownload={downloadJob}
                onOpenDuplicate={handleOpenDuplicate}
              />
            ))}
          </Box>
        </Collapse>
      </Paper>

      {/* Duplicate diff dialog (triggered from widget) */}
      <DuplicateDetectionDialog
        open={dupDialog.open}
        duplicates={dupDialog.job?.duplicate_info?.duplicates || []}
        onCancel={handleCloseDuplicate}
        onOverride={handleOverride}
        onFieldOverride={handleFieldOverride}
        onKeepAll={handleKeepAll}
      />

    </>
  );
}
